import React, { useMemo, useEffect, useState } from 'react'; 
import { Alert } from '../../services/alertService';
import { api } from '../../services/api';
import type { QuoteResponse } from '@catalyst-iq/shared';
import { StatusBadge } from './StatusBadge';
import { Trash2, Clock, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react'; 
import { TickerIcon } from '../TickerIcon';
import ConfirmDialog from '../shared/ConfirmDialog';

interface Props {
  alerts: Alert[];
  loading: boolean;
  onDelete: (id: string) => Promise<void>;
  onRecreate?: (alert: Alert) => void;
  recreatingId?: string | null;
}

interface QuoteState {
  [ticker: string]: QuoteResponse | null;
}

type AlertSortField =
  | 'ticker'
  | 'industry'
  | 'condition'
  | 'target'
  | 'current'
  | 'progress'
  | 'status'
  | 'time';

const CHANNEL_CONFIG: Record<string, { label: string; icon: string; className: string }> = {
  telegram: {
    label: 'Telegram',
    icon: '✈️',
    className: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20'
  },
  in_app: {
    label: 'In-App',
    icon: '🔔',
    className: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20'
  }
};

const STATUS_SORT_ORDER: Record<string, number> = {
  active: 0,
  triggered: 1,
  cancelled: 2,
};

const TH = ({
  children,
  right,
  compact,
  onClick,
}: {
  children?: React.ReactNode;
  right?: boolean;
  compact?: boolean;
  onClick?: () => void;
}) => (
  <th
    className={`${compact ? 'px-2 w-10' : 'px-5'} py-3 text-[10.5px] font-semibold tracking-[0.08em] uppercase text-gray-400 dark:text-[#a3b6cc] whitespace-nowrap ${right ? 'text-right' : 'text-left'} ${onClick ? 'cursor-pointer select-none hover:text-gray-600 dark:hover:text-[#c2d0e0] group' : ''}`}
    onClick={onClick}
  >
    {children}
  </th>
);

function getAlertDisplayTime(alert: Alert): number {
  const isTriggered = alert.status === 'triggered';
  const isCancelled = alert.status === 'cancelled';
  const value = isTriggered
    ? alert.triggered_at
    : (isCancelled && (alert as any).cancelled_at ? (alert as any).cancelled_at : alert.created_at);
  const time = value ? new Date(value).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
}

function getCurrentNumericValue(alert: Alert, quote: QuoteResponse | null): number {
  if (!quote) return -Infinity;
  if (alert.alert_type.includes('pct')) return quote.change_pct ?? -Infinity;
  return quote.price ?? -Infinity;
}

function formatTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCurrentValue(alert: Alert, quote: QuoteResponse | null): string {
  if (!quote) return '—';
  if (alert.alert_type.includes('pct')) {
    const value = quote.change_pct ?? 0;
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  }
  return `$${(quote.price ?? 0).toFixed(2)}`;
}

function getProgress(alert: Alert, quote: QuoteResponse | null): number {
  if (!quote) return 0;
  const target = alert.target_value;

  if (alert.alert_type === 'price_above') {
    if (!quote.price || target === 0) return 0;
    return (quote.price / target) * 100;
  }

  if (alert.alert_type === 'price_below') {
    if (!quote.price || target === 0) return 0;
    if (quote.price <= target) return 100;
    return (target / quote.price) * 100;
  }

  if (alert.alert_type === 'change_pct_above') {
    if (target === 0) return 0;
    return (quote.change_pct / target) * 100;
  }

  return 0;
}

function getProgressColor(progress: number): string {
  if (progress >= 100) return 'bg-emerald-400';
  if (progress > 80) return 'bg-amber-400';
  if (progress > 50) return 'bg-cyan-400';
  return 'bg-slate-400';
}

export function AlertsList({ alerts, loading, onDelete, onRecreate, recreatingId = null }: Props) {
  const [expandedAlertId, setExpandedAlertId] = useState<string | null>(null);
  const [quotes, setQuotes] = useState<QuoteState>({});
  const [pendingDelete, setPendingDelete] = useState<Alert | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sortField, setSortField] = useState<AlertSortField>('time');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const toggleExpand = (id: string) => {
    setExpandedAlertId(expandedAlertId === id ? null : id);
  };

  const handleSort = (field: AlertSortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderArrow = (field: AlertSortField) => {
    if (sortField !== field) {
      return <span className="opacity-20 ml-1 transition-opacity group-hover:opacity-60">↕</span>;
    }
    return sortOrder === 'asc'
      ? <span className="text-[#a78bfa] ml-1 font-sans">▲</span>
      : <span className="text-[#f5476a] ml-1 font-sans">▼</span>;
  };

  const SortLabel = ({ field, children, right }: { field: AlertSortField; children: React.ReactNode; right?: boolean }) => (
    <div className={`flex items-center pointer-events-none ${right ? 'justify-end' : ''}`}>
      {children} {renderArrow(field)}
    </div>
  );

  useEffect(() => {
    const tickers = Array.from(new Set(alerts.map((alert) => alert.ticker).filter(Boolean)));
    if (tickers.length === 0) {
      setQuotes({});
      return;
    }

    let cancelled = false;

    async function loadQuotes() {
      const results: QuoteState = {};
      await Promise.all(
        tickers.map(async (ticker) => {
          try {
            results[ticker] = await api.getQuote(ticker);
          } catch {
            results[ticker] = null;
          }
        })
      );

      if (!cancelled) {
        setQuotes(results);
      }
    }

    void loadQuotes();
    return () => {
      cancelled = true;
    };
  }, [alerts]);

  const sortedAlerts = useMemo(() => {
    const dir = sortOrder === 'asc' ? 1 : -1;

    return [...alerts].sort((a, b) => {
      switch (sortField) {
        case 'ticker':
          return dir * a.ticker.localeCompare(b.ticker);
        case 'industry': {
          const indA = (a.industry && a.industry !== 'Unknown' ? a.industry : '') || '';
          const indB = (b.industry && b.industry !== 'Unknown' ? b.industry : '') || '';
          return dir * indA.localeCompare(indB);
        }
        case 'condition':
          return dir * a.alert_type.localeCompare(b.alert_type);
        case 'target':
          return dir * (a.target_value - b.target_value);
        case 'current': {
          const valA = getCurrentNumericValue(a, quotes[a.ticker] ?? null);
          const valB = getCurrentNumericValue(b, quotes[b.ticker] ?? null);
          return dir * (valA - valB);
        }
        case 'progress': {
          const valA = getProgress(a, quotes[a.ticker] ?? null);
          const valB = getProgress(b, quotes[b.ticker] ?? null);
          return dir * (valA - valB);
        }
        case 'status': {
          const statusA = STATUS_SORT_ORDER[a.status] ?? 99;
          const statusB = STATUS_SORT_ORDER[b.status] ?? 99;
          return dir * (statusA - statusB);
        }
        case 'time':
        default:
          return dir * (getAlertDisplayTime(a) - getAlertDisplayTime(b));
      }
    });
  }, [alerts, quotes, sortField, sortOrder]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-[#1d2d47] bg-white dark:bg-[#0e1624] py-14 flex flex-col items-center gap-3">
        <div className="w-5 h-5 border-2 border-gray-200 dark:border-[#263757] border-t-[#a78bfa] rounded-full animate-spin" />
        <span className="text-[13px] text-gray-400 dark:text-[#a3b6cc]">Loading alerts…</span>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-[#1d2d47] bg-white dark:bg-[#0e1624] overflow-hidden shadow-sm">
        <div className="h-[3px] bg-gradient-to-r from-[#a78bfa] via-[#818cf8] to-[#4f8ff8]" />
        <div className="py-14 flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-violet-50 dark:bg-[#a78bfa]/10 flex items-center justify-center">
            <svg className="w-5 h-5 text-violet-400 dark:text-[#a78bfa]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
          </div>
          <p className="text-[13px] text-gray-400 dark:text-[#a3b6cc]">No alerts match this filter</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="rounded-2xl border border-gray-200 dark:border-[#1d2d47] bg-white dark:bg-[#0e1624] overflow-hidden shadow-sm w-full">
      <div className="h-[3px] bg-gradient-to-r from-[#a78bfa] via-[#818cf8] to-[#4f8ff8]" />

      <div className="table-scroll">
        <table className="w-full text-[13px] border-collapse min-w-[960px]">
          <thead>
            <tr className="border-b border-gray-100 dark:border-[#1d2d47]/70 bg-gray-50/50 dark:bg-[#0b111c]">
              <TH onClick={() => handleSort('ticker')}><SortLabel field="ticker">Ticker</SortLabel></TH>
              <TH right onClick={() => handleSort('industry')}><SortLabel field="industry" right>Industry</SortLabel></TH>
              <TH onClick={() => handleSort('condition')}><SortLabel field="condition">Condition</SortLabel></TH>
              <TH onClick={() => handleSort('target')}><SortLabel field="target">Target</SortLabel></TH>
              <TH onClick={() => handleSort('current')}><SortLabel field="current">Current</SortLabel></TH>
              <TH onClick={() => handleSort('progress')}><SortLabel field="progress">Progress</SortLabel></TH>
              <TH onClick={() => handleSort('status')}><SortLabel field="status">Status</SortLabel></TH>
              <TH onClick={() => handleSort('time')}><SortLabel field="time">Time</SortLabel></TH>
              <TH right compact />
            </tr>
          </thead>
          <tbody>
            {sortedAlerts.map((alert, i) => {
              const isTriggered = alert.status === 'triggered';
              const isCancelled = alert.status === 'cancelled';
              const isActive = alert.status === 'active';
              const isExpanded = expandedAlertId === alert.id;

              const deliveries = (alert as any).alert_deliveries || [];
              const isExpandable = isTriggered || isActive;

              const actualChannels: string[] = (() => {
                const raw = (alert as any).channels || (alert as any).delivery;

                if (Array.isArray(raw)) {
                  return raw;
                }

                if (raw && typeof raw === 'object') {
                  return Object.entries(raw)
                    .filter(([_, enabled]) => enabled === true)
                    .map(([channelName]) => channelName);
                }

                return [];
              })();

              const configuredChannels = actualChannels.filter((channelName, index) => {
                const normalized = channelName.toLowerCase();
                return actualChannels.findIndex((item) => item.toLowerCase() === normalized) === index;
              });

              const uniqueDeliveries = deliveries.reduce((result: any[], delivery: any) => {
                const channelKey = String(delivery.channel || '').toLowerCase();
                if (!channelKey || result.some((item) => String(item.channel || '').toLowerCase() === channelKey)) {
                  return result;
                }

                result.push(delivery);
                return result;
              }, []);

              const displayTime = isTriggered 
                ? alert.triggered_at 
                : (isCancelled && (alert as any).cancelled_at ? (alert as any).cancelled_at : (alert as any).created_at);

              return (
                <React.Fragment key={alert.id}>
                  <tr
                    onClick={() => isExpandable && toggleExpand(alert.id)}
                    className={`
                      border-b border-gray-50 dark:border-[#1d2d47]/40 last:border-0
                      transition-colors duration-100 select-none
                      ${isExpandable ? 'cursor-pointer' : 'cursor-default'}
                      ${isTriggered ? 'bg-amber-50/60 dark:bg-amber-400/[0.06]' : ''}
                      ${!isTriggered && !isCancelled && i % 2 !== 0 ? 'bg-gray-100/40 dark:bg-[#0b111c]/30' : ''}
                      ${isExpanded ? 'bg-violet-50/20 dark:bg-[#a78bfa]/5' : 'hover:bg-violet-50/30 dark:hover:bg-[#a78bfa]/5'}
                    `}
                  >
                    <td className="px-5 py-3.5 relative">
                      {isTriggered && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-300 rounded-r-full z-10" />
                      )}
                      <div className="flex items-center gap-2">
                        <TickerIcon ticker={alert.ticker} size="sm" />
                        <span className={`font-mono font-bold tracking-wide ${
                          isTriggered ? 'text-amber-500 dark:text-amber-300'
                          : isCancelled ? 'text-gray-400 dark:text-[#a3b6cc]'
                          : 'text-[#4f8ff8]'
                        }`}>
                          {alert.ticker}
                        </span>
                        
                        {isExpandable && (
                          <span className="text-gray-400 dark:text-[#a3b6cc] ml-1">
                            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-5 py-3.5 text-[12px] text-right text-gray-600 dark:text-[#c2d0e0]">
                      {(alert as any).industry && (alert as any).industry !== 'Unknown' ? (alert as any).industry : '-'}
                    </td>

                    <td className="px-5 py-3.5">
                      <span className={`text-[10px] font-semibold uppercase tracking-[0.1em] ${
                        isCancelled ? 'text-gray-400 dark:text-[#a3b6cc]/70' : 'text-gray-400 dark:text-[#a3b6cc]'
                      }`}>
                        {alert.alert_type.replace(/_/g, ' ')}
                      </span>
                    </td>

                          <td className={`px-5 py-3.5 font-mono font-semibold tabular-nums ${
                      isCancelled ? 'text-gray-400 dark:text-[#a3b6cc]' : 'text-gray-800 dark:text-[#d5deea]'
                    }`}>
                      {alert.alert_type.includes('pct')
                        ? `${alert.target_value}%`
                        : `$${alert.target_value.toFixed(2)}`}
                    </td>

                    <td className={`px-5 py-3.5 font-mono font-semibold text-right tabular-nums ${isCancelled ? 'text-gray-400 dark:text-[#a3b6cc]' : 'text-gray-800 dark:text-[#d5deea]'}`}>
                      {formatCurrentValue(alert, quotes[alert.ticker] ?? null)}
                    </td>

                    <td className="px-5 py-3.5">
                      {(() => {
                        const progress = getProgress(alert, quotes[alert.ticker] ?? null);
                        const capped = Math.min(100, Math.max(0, progress));
                        const progressLabel = quotes[alert.ticker] ? `${Math.round(capped)}% TO THRESHOLD` : 'Loading quote...';

                        return (
                          <div className="space-y-2">
                            <div className="relative h-2 rounded-full bg-slate-200 dark:bg-[#1b2638] overflow-hidden">
                              <div
                                className={`absolute inset-y-0 left-0 rounded-full ${getProgressColor(capped)}`}
                                style={{ width: `${capped}%` }}
                              />
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.16em] text-gray-400">{progressLabel}</div>
                          </div>
                        );
                      })()}
                    </td>

                    <td className="px-5 py-3.5">
                      <StatusBadge status={alert.status} />
                    </td>

                    <td className="px-5 py-3.5">
                      <div className={`flex items-center gap-1 text-[11px] font-mono ${
                        isTriggered ? 'text-amber-600 dark:text-amber-400/80' : 'text-gray-300 dark:text-[#6d82a0]'
                      }`}>
                        <Clock size={11} />
                        {formatTime(displayTime)}
                      </div>
                    </td>

                    <td className="px-2 w-10 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {isTriggered && onRecreate && (
                          <button
                            type="button"
                            onClick={() => onRecreate(alert)}
                            disabled={!!recreatingId}
                            aria-busy={recreatingId === alert.id}
                            className="p-1.5 rounded-lg text-amber-500 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-400/10 transition-all duration-150 disabled:opacity-40 disabled:pointer-events-none"
                          >
                            <RotateCcw size={14} className={recreatingId === alert.id ? 'animate-spin' : undefined} />
                          </button>
                        )}
                        <button
                          onClick={() => setPendingDelete(alert)}
                          className="p-1.5 rounded-lg text-gray-300 dark:text-[#a3b6cc] hover:text-red-500 dark:hover:text-[#f5476a] hover:bg-red-50 dark:hover:bg-[#f5476a]/10 transition-all duration-150"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* שורת הפירוט המעודכנת - נקייה, קומפקטית, LTR ובאנגלית בלבד */}
                  {isExpanded && (
                    <tr className="bg-gray-50/30 dark:bg-[#0b111c]/40 border-b border-gray-100 dark:border-[#1d2d47]/30">
                      <td colSpan={9} className="px-8 py-3.5 animate-fadeIn">
                        <div className="flex flex-col gap-2 text-left" dir="ltr">
                          
                          <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-gray-400 dark:text-[#a3b6cc] mb-1">
                            {deliveries.length > 0 ? 'Delivery Log' : 'Configured Channels'}
                          </div>
                          
                          {isActive ? (
                            configuredChannels.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {configuredChannels.map((channelName: string, idx: number) => {
                                  const channelInfo = CHANNEL_CONFIG[channelName.toLowerCase()] || {
                                    label: channelName,
                                    icon: '📱',
                                    className: 'bg-gray-100 dark:bg-[#1d2d47] text-gray-500 border-transparent'
                                  };

                                  return (
                                    <span
                                      key={`${channelName}-${idx}`}
                                      className={`inline-flex items-center gap-1.5 font-bold text-[11px] px-2 py-0.5 rounded border ${channelInfo.className}`}
                                    >
                                      <span>{channelInfo.icon}</span>
                                      <span>{channelInfo.label}</span>
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-[11px] text-gray-400 dark:text-[#a3b6cc] italic pl-1">
                                No notification channels selected for this alert.
                              </div>
                            )
                          ) : uniqueDeliveries.length > 0 ? (
                            <div className="flex flex-col gap-1.5">
                              {uniqueDeliveries.map((delivery: any, idx: number) => {
                                const channelInfo = CHANNEL_CONFIG[delivery.channel?.toLowerCase?.() || ''] || {
                                  label: delivery.channel || 'Unknown',
                                  icon: '📱',
                                  className: 'bg-gray-100 dark:bg-[#1d2d47] text-gray-500 border-transparent'
                                };

                                return (
                                  <div
                                    key={`${delivery.channel || 'channel'}-${idx}`}
                                    className="flex items-center gap-3 px-3 py-1.5 rounded-xl bg-white dark:bg-[#0e1624] border border-gray-100 dark:border-[#1d2d47]/60 text-[12px] font-sans w-fit"
                                  >
                                    <span className={`inline-flex items-center gap-1.5 font-mono font-bold uppercase text-[10px] tracking-wider px-2 py-0.5 rounded border ${channelInfo.className}`}>
                                      <span>{channelInfo.icon}</span>
                                      <span>{channelInfo.label}</span>
                                    </span>

                                    <span className={`font-semibold text-[11px] px-1.5 py-0.5 rounded ${
                                      delivery.status === 'sent' ? 'text-green-500 bg-green-500/10' :
                                      delivery.status === 'failed' ? 'text-red-500 bg-red-500/10' :
                                      'text-amber-500 bg-amber-500/10'
                                    }`}>
                                      {delivery.status === 'pending' && alert.status === 'active'
                                        ? 'WAITING'
                                        : String(delivery.status || 'unknown').toUpperCase()}
                                    </span>

                                    <span className="text-gray-400 dark:text-[#a3b6cc] font-mono text-[11px]">
                                      {formatTime(delivery.sent_at || delivery.created_at)}
                                    </span>

                                    {delivery.status === 'failed' && delivery.failure_reason && (
                                      <span className="text-red-400 text-[11px] bg-red-500/5 px-2 py-0.5 rounded border border-red-500/10 font-medium">
                                        Reason: {delivery.failure_reason}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            /* הגנה למקרה שלא נבחרו ערוצי הפצה בכלל */
                            <div className="text-[11px] text-gray-400 dark:text-[#a3b6cc] italic pl-1">
                              No notification channels selected for this alert.
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>

    <ConfirmDialog
      open={!!pendingDelete}
      tone="danger"
      title={pendingDelete ? `Cancel alert for ${pendingDelete.ticker}?` : 'Cancel alert?'}
      description="This alert will be cancelled and stop monitoring. You can recreate it later if needed."
      confirmLabel="Cancel Alert"
      cancelLabel="Keep"
      loading={deleting}
      onCancel={() => {
        if (deleting) return;
        setPendingDelete(null);
      }}
      onConfirm={async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        try {
          await onDelete(pendingDelete.id);
          setPendingDelete(null);
        } finally {
          setDeleting(false);
        }
      }}
    />
    </>
  );
}